import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kitchenApi } from '../services/api';
import { ArrowLeft, Clock, Check, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';

interface HistoryTicket {
  id: string;
  orderNumber: number;
  tableNumber: string | null;
  serverName: string | null;
  deliveredAt: string;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
  }>;
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: history, isLoading } = useQuery({
    queryKey: ['kitchen-history'],
    queryFn: async () => {
      const response = await kitchenApi.getHistory(50);
      return (response.data?.data || response.data || []) as HistoryTicket[];
    },
    refetchInterval: 15000,
  });

  // Recall: an accidental bump must be recoverable — the ticket goes back to
  // the board as READY
  const recallMutation = useMutation({
    mutationFn: async (ticketId: string) => {
      const response = await kitchenApi.recallTicket(ticketId);
      if (!(response.data?.success ?? true)) {
        throw new Error('Recall failed');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-history'] });
      queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });
      navigate('/');
    },
  });

  const formatTime = (dateStr: string) => {
    return format(new Date(dateStr), 'h:mm a');
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-4 flex items-center gap-3 safe-top">
        <button
          onClick={() => navigate('/')}
          className="p-3 rounded-xl bg-slate-700 active:bg-slate-600 transition-colors btn-press"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-xl">Ticket History</h1>
        <span className="text-sm text-slate-500 ml-auto">today</span>
      </header>

      {/* History List */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="min-h-[calc(100%+1px)] p-3 sm:p-4 safe-bottom">
        {isLoading ? (
          <div className="text-center text-slate-500 py-12">
            <div className="w-8 h-8 border-2 border-slate-600 border-t-primary-500 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-base">Loading history...</p>
          </div>
        ) : !history?.length ? (
          <div className="text-center text-slate-500 py-12">
            <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No bumped tickets yet today</p>
            <p className="text-sm mt-1 text-slate-600">Bumped tickets land here — recall one if it was a mis-tap</p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-slate-800 rounded-xl p-4 sm:p-5 border border-slate-700"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg">
                      {ticket.tableNumber ? `Table ${ticket.tableNumber}` : `#${ticket.orderNumber}`}
                    </span>
                    <span className="flex items-center gap-2 bg-emerald-900/50 px-2.5 py-1 rounded-lg text-emerald-400 text-sm">
                      <Check className="w-4 h-4" />
                      bumped {formatTime(ticket.deliveredAt)}
                    </span>
                  </div>
                  <button
                    onClick={() => recallMutation.mutate(ticket.id)}
                    disabled={recallMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-700 text-slate-200 text-sm font-medium active:bg-slate-600 disabled:opacity-50"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Recall
                  </button>
                </div>

                {/* Items */}
                <div className="text-sm sm:text-base text-slate-400 mb-1">
                  {ticket.items
                    .map((item) => `${item.quantity}x ${item.name}`)
                    .join(', ')}
                </div>

                {/* Footer */}
                {ticket.serverName && (
                  <div className="text-xs sm:text-sm text-slate-500 pt-2 border-t border-slate-700/50">
                    {ticket.serverName}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
